import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { queryString } from "lib/siteCustomerApi";
import {
  date,
  Remote,
  Pager,
  Receipt,
  useOperation,
  useRemote,
} from "./Shared";
import { EmailRequest } from "./SignIn";
export function Profile({ profile, onRefresh }) {
  const [name, setName] = useState(profile.name),
    [marketing, setMarketing] = useState(profile.marketingConsent),
    [kind, setKind] = useState("export"),
    [message, setMessage] = useState("");
  const op = useOperation(),
    privacy = useOperation();
  return (
    <>
      <p className="ca-eyebrow">Account</p>
      <h1>Profile &amp; preferences</h1>
      <div className="ca-columns">
        <section className="ca-panel">
          <h2>Your details</h2>
          <p className="ca-email">
            Verified email: <strong>{profile.email}</strong>
          </p>
          <form
            className="ca-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (
                await op.run(
                  "/customer/profile",
                  { name, marketingConsent: marketing },
                  "PATCH",
                )
              )
                await onRefresh();
            }}
          >
            <label>
              Name
              <input
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="ca-check">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
              />
              Send me occasional Magellan updates.
            </label>
            <p className="ca-muted">
              Sign-in and support emails are transactional and do not depend on
              this preference.
            </p>
            <Receipt op={op} />
            <button className="ca-primary" disabled={op.busy}>
              Save profile
            </button>
          </form>
        </section>
        <section className="ca-panel">
          <h2>Change your email</h2>
          <p>
            Verify the new address using the email we send. Keep this browser
            signed in. Changing your email ends all older website sessions.
          </p>
          <EmailRequest purpose="email_change" />
        </section>
      </div>
      <section className="ca-panel">
        <h2>Your data</h2>
        <p>
          Submit a privacy request for our team to review. Deletion is not
          immediate.
        </p>
        <form
          className="ca-form"
          onSubmit={async (e) => {
            e.preventDefault();
            await privacy.run("/customer/privacy", { kind, message });
          }}
        >
          <label>
            Request type
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="export">Request a copy of my data</option>
              <option value="correction">Request a correction</option>
              <option value="deletion">Request deletion</option>
            </select>
          </label>
          <label>
            Details
            <textarea
              maxLength={4000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <Receipt op={privacy}>Your request was recorded for review.</Receipt>
          <button disabled={privacy.busy}>Submit privacy request</button>
        </form>
      </section>
    </>
  );
}
export function Sessions({ onRefresh }) {
  const [pages, setPages] = useState([null]),
    [tick, setTick] = useState(0),
    [target, setTarget] = useState(null),
    op = useOperation();
  const state = useRemote(
    "/customer/sessions?" + queryString({ after: pages.at(-1), limit: 20 }),
    tick,
  );
  return (
    <>
      <p className="ca-eyebrow">Security</p>
      <h1>Website sessions</h1>
      <p>
        Control sign-in sessions for this website. CarrierFlow device security
        remains in your CarrierFlow account.
      </p>
      <Receipt op={op} />
      {target && (
        <section className="ca-panel">
          <h2>End this session?</h2>
          <p>
            {target.current
              ? "This will sign you out of this browser."
              : "That session will need a fresh email sign-in."}
          </p>
          <div className="ca-actions">
            <button
              disabled={op.busy}
              onClick={async () => {
                if (
                  await op.run("/customer/revoke-session", {
                    sessionId: target.id,
                  })
                ) {
                  setTarget(null);
                  setTick((v) => v + 1);
                  await onRefresh();
                }
              }}
            >
              Confirm end session
            </button>
            <button onClick={() => setTarget(null)}>Keep session</button>
          </div>
        </section>
      )}
      <Remote state={state}>
        {state.data && (
          <>
            <div className="ca-list">
              {state.data.items.map((row) => (
                <article className="ca-panel ca-section-head" key={row.id}>
                  <div>
                    <h2>{row.current ? "This browser" : "Website session"}</h2>
                    <p>Signed in {date(row.createdAt)}</p>
                    <p>Expires {date(row.expiresAt)}</p>
                  </div>
                  {row.revokedAt ? (
                    <span>Ended {date(row.revokedAt)}</span>
                  ) : row.status === "invalidated" ? (
                    <span>Ended after an account change</span>
                  ) : row.expiresAt <= Date.now() ? (
                    <span>Expired</span>
                  ) : (
                    <button onClick={() => setTarget(row)}>End session</button>
                  )}
                </article>
              ))}
            </div>
            <Pager pages={pages} setPages={setPages} next={state.data.next} />
          </>
        )}
      </Remote>
    </>
  );
}
export function Activity() {
  const [pages, setPages] = useState([null]),
    state = useRemote(
      "/customer/activity?" + queryString({ after: pages.at(-1), limit: 25 }),
    );
  const actions = {
    "google.sign_in": "Signed in with Google",
    "ticket.issued": "File access requested",
    "preview.started": "Preview opened",
    "download.started": "Download started",
    profile: "Profile updated",
    logout: "Signed out",
    "revoke-session": "Session ended",
  };
  return (
    <>
      <p className="ca-eyebrow">Your history</p>
      <h1>Account activity</h1>
      <p>
        A download starting does not confirm that a file was saved on your
        device.
      </p>
      <Remote state={state}>
        {state.data && (
          <>
            {!state.data.items.length ? (
              <p className="ca-panel">No activity on this page.</p>
            ) : (
              <ol className="ca-timeline">
                {state.data.items.map((row) => (
                  <li key={row.id}>
                    <strong>
                      {actions[row.action] || row.action.replaceAll(".", " ")}
                    </strong>
                    <time>{date(row.at)}</time>
                    <small>Reference: {row.target}</small>
                  </li>
                ))}
              </ol>
            )}
            <Pager pages={pages} setPages={setPages} next={state.data.next} />
          </>
        )}
      </Remote>
    </>
  );
}
export function Support() {
  const [pages, setPages] = useState([null]),
    [status, setStatus] = useState(""),
    [subject, setSubject] = useState(""),
    [message, setMessage] = useState("");
  const op = useOperation(),
    nav = useNavigate();
  const state = useRemote(
    "/customer/support?" +
      queryString({ after: pages.at(-1), status, limit: 20 }),
  );
  return (
    <>
      <p className="ca-eyebrow">We're here to help</p>
      <h1>Support</h1>
      <div className="ca-columns">
        <section className="ca-panel">
          <h2>New request</h2>
          <form
            className="ca-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const result = await op.run("/customer/support", {
                subject,
                message,
              });
              if (result) nav("/customer/access/support/" + result.id);
            }}
          >
            <label>
              Subject
              <input
                required
                maxLength={200}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>
            <label>
              Message
              <textarea
                required
                maxLength={10000}
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>
            <p className="ca-muted">
              Do not include passwords, login links or payment card details.
            </p>
            <Receipt op={op} />
            <button className="ca-primary" disabled={op.busy}>
              Send request
            </button>
          </form>
        </section>
        <section>
          <h2>Your requests</h2>
          <label>
            Status
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPages([null]);
              }}
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="waiting">Waiting for you</option>
              <option value="closed">Closed</option>
            </select>
          </label>
          <Remote state={state}>
            {state.data && (
              <>
                {!state.data.items.length && <p>No requests on this page.</p>}
                {state.data.items.map((row) => (
                  <article className="ca-panel" key={row.id}>
                    <h3>
                      <Link to={"/customer/access/support/" + row.id}>
                        {row.subject}
                      </Link>
                    </h3>
                    <p>
                      {row.status} · {date(row.createdAt)}
                    </p>
                  </article>
                ))}
                <Pager
                  pages={pages}
                  setPages={setPages}
                  next={state.data.next}
                />
              </>
            )}
          </Remote>
        </section>
      </div>
    </>
  );
}
export function SupportThread() {
  const { ticketId } = useParams(),
    [pages, setPages] = useState([null]),
    [tick, setTick] = useState(0),
    [message, setMessage] = useState(""),
    op = useOperation();
  const path =
      "/customer/support/" + encodeURIComponent(ticketId) + "/messages",
    state = useRemote(
      path + "?" + queryString({ after: pages.at(-1), limit: 30 }),
      tick,
    );
  return (
    <>
      <Link to="/customer/access/support">← All requests</Link>
      <h1>Support conversation</h1>
      <Remote state={state}>
        {state.data && (
          <>
            <ol className="ca-timeline">
              {state.data.items.map((row) => (
                <li key={row.id}>
                  <strong>
                    {row.actorType === "customer" ? "You" : "Magellan support"}
                  </strong>
                  <time>{date(row.createdAt)}</time>
                  <p className="ca-message">{row.message}</p>
                </li>
              ))}
            </ol>
            <Pager pages={pages} setPages={setPages} next={state.data.next} />
            <form
              className="ca-form ca-panel"
              onSubmit={async (e) => {
                e.preventDefault();
                if (await op.run(path, { message })) {
                  setMessage("");
                  setPages([null]);
                  setTick((v) => v + 1);
                }
              }}
            >
              <label>
                Reply
                <textarea
                  required
                  rows={5}
                  maxLength={10000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </label>
              <Receipt op={op}>Your reply was recorded.</Receipt>
              <button className="ca-primary" disabled={op.busy}>
                Send reply
              </button>
            </form>
          </>
        )}
      </Remote>
    </>
  );
}
